import calculateBalance, {
  clearBalanceMemo,
} from '../../Impounds/helpers/calculateBalance';
import calculateScheduledDistribution, {
  clearScheduledDistributionMemo,
} from '../../Impounds/helpers/calculateScheduledDistribution';
import sumDollarAmounts from '../../../../../../helpers/sumDollarAmounts';
import getCalculatedBalance from './getCalculatedBalance';

// Find the first "pending" invoice after the selected row OR if the selected row is "pending" use it. Display invoice.date date
function getStartingInvoice({ invoiceId, invoices }) {
  const currentInvoiceIndex = invoices.findIndex(
    (invoice) => invoice.id === +invoiceId
  );

  if (currentInvoiceIndex === -1) {
    throw new Error('Invoice not found');
  }

  if (invoices[currentInvoiceIndex].status === 'pending') {
    return {
      invoice: invoices[currentInvoiceIndex],
      index: currentInvoiceIndex,
    };
  }

  const firstPendingInvoiceAfterCurrentIndex = invoices.findIndex(
    (invoice) => invoice.status === 'pending'
  );

  if (firstPendingInvoiceAfterCurrentIndex === -1) {
    throw new Error('No pending invoices found');
  }

  const startingInvoice = invoices[firstPendingInvoiceAfterCurrentIndex];

  return {
    invoice: {
      date: startingInvoice.date,
      sequence: startingInvoice.sequence,
      pixel_invoice_number: startingInvoice.pixel_invoice_number,
      id: startingInvoice.id,
    },
    index: firstPendingInvoiceAfterCurrentIndex,
  };
}

// The initial impound balance is the value in the balance column on the row preceding the starting invoice
function getInitialImpoundBalance({ invoices, bills, startingInvoiceIndex }) {
  const prevInvoice = invoices[startingInvoiceIndex - 1];

  if (!prevInvoice) {
    throw new Error('No previous invoice found');
  }

  const initialImpoundBalance = calculateBalance({
    row: prevInvoice,
    sortedInvoices: invoices,
    bills,
  });

  clearBalanceMemo();

  return initialImpoundBalance;
}

// Find the total of all "Distributions" in the 12 months starting with the starting invoice. Divide by 12 and round to nearest 2 decimals. For taxPayment, insurancePayment, and hoaPayment, use the same logic but filter by billType
function getMonthlyImpoundPayment({
  startingInvoiceIndex,
  bills,
  invoices,
  billType,
}) {
  const twelveMonthsInvoices = invoices.slice(
    startingInvoiceIndex,
    startingInvoiceIndex + 12
  );

  const distributions = twelveMonthsInvoices.map((invoice) => {
    return {
      amount: calculateScheduledDistribution({
        row: invoice,
        bills,
        shouldReturnRawValue: true,
        billType,
      }).sum,
    };
  });

  const total = sumDollarAmounts(distributions, 'amount');

  return (total / 12).toFixed(2);
}

export default function getImpoundAnalysisData({
  invoiceId,
  invoices,
  bills,
  loanCushion,
}) {
  const invoicesWithoutProjected = invoices.filter(
    (invoice) => invoice.sequence !== 'PROJECTED' && invoice.sequence !== ''
  );

  const { invoice: startingInvoice, index: startingInvoiceIndex } =
    getStartingInvoice({ invoiceId, invoices: invoicesWithoutProjected });

  const initialImpoundBalance = getInitialImpoundBalance({
    invoices: invoicesWithoutProjected,
    bills,
    startingInvoiceIndex,
  });

  const totalMonthlyImpoundPayment = getMonthlyImpoundPayment({
    startingInvoiceIndex,
    bills,
    invoices: invoicesWithoutProjected,
  });

  const taxPayment = getMonthlyImpoundPayment({
    startingInvoiceIndex,
    bills,
    invoices: invoicesWithoutProjected,
    billType: 'Tax',
  });

  const insurancePayment = getMonthlyImpoundPayment({
    startingInvoiceIndex,
    bills,
    invoices: invoicesWithoutProjected,
    billType: 'Insurance',
  });

  const hoaPayment = getMonthlyImpoundPayment({
    startingInvoiceIndex,
    bills,
    invoices: invoicesWithoutProjected,
    billType: 'HOA',
  });

  clearScheduledDistributionMemo();

  const totalCushion = (loanCushion * totalMonthlyImpoundPayment).toFixed(2);

  const calculatedBalance = getCalculatedBalance({
    invoices: invoicesWithoutProjected,
    startingInvoiceIndex,
    initialImpoundBalance,
    totalMonthlyImpoundPayment,
    bills,
  });

  const adjustment = totalCushion - Math.min(...calculatedBalance);

  return {
    startingInvoice,
    startingInvoiceIndex,
    initialImpoundBalance,
    totalMonthlyImpoundPayment,
    taxPayment,
    insurancePayment,
    hoaPayment,
    totalCushion,
    adjustment,
  };
}
